import type { CourseLeaf, CourseNavigation } from "../../../lib/learning/types";
import type { RoadmapData, RoadmapStep, RoadmapStepStatus } from "./types";

function leafStatusToStepStatus(leaf: CourseLeaf, isCurrent: boolean): RoadmapStepStatus {
  if (leaf.status === "completed") return "completed";
  if (leaf.status === "locked") return "locked";
  return isCurrent ? "current" : "available";
}

/** Derive resume index: current → latest unlocked → latest completed → 0. */
export function resolveInitialStepIndex(steps: RoadmapStep[]): number {
  const checkpoints = steps.filter((s) => s.kind === "checkpoint");
  if (checkpoints.length === 0) return 0;

  const currentIdx = steps.findIndex((s) => s.status === "current");
  if (currentIdx >= 0) return currentIdx;

  let lastUnlocked = -1;
  let lastCompleted = -1;
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    if (!s || s.kind !== "checkpoint") continue;
    if (!s.locked) lastUnlocked = i;
    if (s.status === "completed") lastCompleted = i;
  }
  if (lastUnlocked >= 0) return lastUnlocked;
  if (lastCompleted >= 0) return lastCompleted;
  return steps.findIndex((s) => s.kind === "checkpoint");
}

/** Map backend course navigation → generic RoadmapData. */
export function courseNavigationToRoadmap(nav: CourseNavigation): RoadmapData {
  const bySubject = new Map<string, CourseLeaf[]>();
  for (const leaf of nav.leaves) {
    const list = bySubject.get(leaf.subject) ?? [];
    list.push(leaf);
    bySubject.set(leaf.subject, list);
  }

  const firstAvailableId =
    nav.leaves.find((l) => l.status === "available")?.id ??
    nav.leaves.find((l) => l.status === "completed")?.id;

  const steps: RoadmapStep[] = [
    {
      id: "start",
      kind: "start",
      title: "Start",
      status: "available",
      locked: false,
      icon: "flag",
    },
  ];

  for (const [subject, leaves] of bySubject) {
    const done = leaves.filter((l) => l.status === "completed").length;
    steps.push({
      id: `phase-${subject}`,
      kind: "phase",
      title: subject,
      status: "available",
      locked: false,
      icon: "star",
      phaseDone: done,
      phaseTotal: leaves.length,
    });

    for (const leaf of leaves) {
      const isCurrent = leaf.id === firstAvailableId && leaf.status === "available";
      steps.push({
        id: leaf.id,
        kind: "checkpoint",
        title: leaf.title,
        description: leaf.subject,
        status: leafStatusToStepStatus(leaf, isCurrent),
        locked: leaf.status === "locked",
        icon: "trophy",
        meta: { leaf },
      });
    }
  }

  steps.push({
    id: "finish",
    kind: "finish",
    title: "Finish",
    status: nav.completion_percent >= 100 ? "completed" : "locked",
    locked: nav.completion_percent < 100,
    icon: "flag",
  });

  return {
    id: nav.pathway_id,
    title: "Roadmap",
    steps,
    progressPercent: nav.completion_percent,
  };
}

export function checkpointCount(steps: RoadmapStep[]): number {
  return steps.filter((s) => s.kind === "checkpoint" || s.kind === "phase").length;
}
